import { CardWrapper, StyledTabPanel } from './AIInsights.styles';
import {
    Alert,
    Grid,
    PargodaInfoIcon,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '../../../base';
import { useTranslation } from 'react-i18next';
import { a11yProps, IClientInsights, TooltipPlacement } from '../../../common';
import { useState } from 'react';
import DOMPurify from 'dompurify';

interface Props {
    insights: IClientInsights;
    isLegacy?: boolean;
}

const AIInsights = (props: Props) => {
    const { insights, isLegacy = false } = props;
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const theme = useTheme();

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: number
    ) => {
        setTabValue(newValue);
    };

    const renderInsights = (renderableInsights: Array<string>) => {
        if (renderableInsights.length === 0) {
            return (
                <Typography variant="body2">
                    {t('jobApplicationPage.noInsightsAvailable')}
                </Typography>
            );
        }
        return (
            <ul style={{ paddingInlineStart: theme.spacing(3), margin: 0 }}>
                {renderableInsights.map((insight) => (
                    <li key={insight}>
                        <Typography
                            variant="body2"
                            component="span"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(insight),
                            }}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <CardWrapper>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ flexShrink: 0 }}
            >
                <Grid
                    item
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                    <Typography variant="h6">
                        {t('jobApplicationPage.AIInsight')}
                    </Typography>
                    <Typography variant="caption">|</Typography>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            flexShrink: 0,
                            '& .Mui-selected': {
                                border: 'none',
                            },
                            paddingBottom: theme.spacing(1),
                            height: '20px',
                            '& .MuiTab-root:not(:last-of-type)': {
                                marginInlineEnd: theme.spacing(2),
                            },
                        }}
                        TabScrollButtonProps={{
                            style: {
                                display: 'none',
                            },
                        }}
                    >
                        <Tab
                            label={t('jobApplicationPage.positive')}
                            {...a11yProps(0)}
                        />
                        <Tab
                            label={t('jobApplicationPage.negative')}
                            {...a11yProps(1)}
                        />
                    </Tabs>
                </Grid>
                <Grid item sx={{ marginInlineEnd: '-5px' }}>
                    <PargodaInfoIcon
                        text={t('jobApplicationPage.aiInsightsInfo')}
                        placement={TooltipPlacement.TOP_START}
                        size={24}
                    />
                </Grid>
            </Grid>
            {isLegacy ? (
                <Alert severity="info" variant="outlined" sx={{ mt: 'auto', mb: 'auto' }}>
                    {t('jobApplicationPage.legacyInsightsNotice')}
                </Alert>
            ) : (
                <StyledTabPanel value={tabValue} index={tabValue}>
                    {tabValue === 0 && renderInsights(insights.positiveInsights)}
                    {tabValue === 1 && renderInsights(insights.negativeInsights)}
                </StyledTabPanel>
            )}
        </CardWrapper>
    );
};

export default AIInsights;
